/**
 * API-Client für /api/locations und /api/shift-templates.
 * Beide werden zusammen behandelt, weil sie eng verzahnt sind (Vorlage gehört zu Standort).
 */

import { apiFetch } from './api';

export interface ApiLocation {
  id: string;
  name: string;
  color: string;
  isActive: boolean;
  companyId: string | null;
  // Phase B (Kapitel 2) — Standort-Stammdaten + Flags.
  address: string | null;
  branchNumber: string | null;
  openingHours: Record<string, unknown> | null;
  managerCanSeeWages: boolean;
  // Phase F (Kapitel 11.6, Entscheidung #16) — eigenes, unabhängiges Recht.
  managerCanSeeBonusHours: boolean;
  allowDoubleShift: boolean;
  allowCrossLocationSwap: boolean;
  // Entscheidung #23 (Kapitel 1.2.2) — rein beschreibend, keine Berechtigungswirkung.
  regionId: string | null;
  // Phase Q (Kapitel 2/19.3, Entscheidung #27) — für die clientseitige
  // Feiertags-Erkennung (feiertagejs). `null` = kein Bundesland gepflegt,
  // dann keine automatische Feiertags-Anzeige im Schichtplan.
  bundesland: Bundesland | null;
  // Nachtrag (Kapitel 3.2, Screen 7, 08.09.2026) — nie der Hash, nur ob eines
  // gesetzt ist (für die Punkte-Anzeige in der Standort-Detail-Ansicht).
  filialPasswordSet: boolean;
  /** v1.3.0: Chef kann das Filialpasswort erneut anzeigen (false = vor v1.3.0 gesetzt → einmal neu setzen). Optional für ältere Server. */
  filialPasswordViewable?: boolean;
  createdAt: string;
  updatedAt: string;
  templates?: ApiShiftTemplate[];
}

/**
 * Phase Q (Kapitel 2/19.3, Entscheidung #27) — die 16 deutschen Bundesländer,
 * spiegelt den `Bundesland`-Enum in backend/prisma/schema.prisma. Bewusst als
 * eigener String-Literal-Typ (nicht importiert) — die Codes sind gleichzeitig
 * mit den `Region`-Codes von `feiertagejs` kompatibel (Teilmenge davon).
 */
export type Bundesland =
  | 'BW'
  | 'BY'
  | 'BE'
  | 'BB'
  | 'HB'
  | 'HH'
  | 'HE'
  | 'MV'
  | 'NI'
  | 'NW'
  | 'RP'
  | 'SL'
  | 'SN'
  | 'ST'
  | 'SH'
  | 'TH';

export const BUNDESLAND_LABELS: Record<Bundesland, string> = {
  BW: 'Baden-Württemberg',
  BY: 'Bayern',
  BE: 'Berlin',
  BB: 'Brandenburg',
  HB: 'Bremen',
  HH: 'Hamburg',
  HE: 'Hessen',
  MV: 'Mecklenburg-Vorpommern',
  NI: 'Niedersachsen',
  NW: 'Nordrhein-Westfalen',
  RP: 'Rheinland-Pfalz',
  SL: 'Saarland',
  SN: 'Sachsen',
  ST: 'Sachsen-Anhalt',
  SH: 'Schleswig-Holstein',
  TH: 'Thüringen',
};

export interface ApiShiftTemplate {
  id: string;
  locationId: string;
  name: string;
  startTime: string; // "HH:MM"
  endTime: string;   // "HH:MM"
  /** Wenn true: keine feste Endzeit (z. B. "Ware"). Mitarbeiter trägt nach. */
  isOpenEnd: boolean;
  isActive: boolean;
  /** Phase Y (Kapitel 20.1, Entscheidung #34) — Anzahl Schichten, die diese
   *  Vorlage nutzen. `null` nur, falls ein Endpunkt sie ausnahmsweise nicht mitliefert. */
  usageCount: number | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Öffnungszeiten (Kapitel 2/19.4) — das Backend akzeptiert bewusst jedes reine
 * JSON-Objekt (kein festes Schema, siehe `OpeningHoursSchema` in
 * backend/src/routes/locations.ts), damit sich das Format bei Bedarf später
 * erweitern lässt. Diese Form (ein Eintrag je Wochentag, `null`/fehlend =
 * geschlossen) ist der Frontend-seitige Vertrag dafür — wird u. a. für den
 * "außerhalb der Öffnungszeiten"-Warnhinweis im Schichtplan gebraucht.
 */
export type WeekdayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
export interface OpeningHoursMap {
  mon?: { open: string; close: string } | null;
  tue?: { open: string; close: string } | null;
  wed?: { open: string; close: string } | null;
  thu?: { open: string; close: string } | null;
  fri?: { open: string; close: string } | null;
  sat?: { open: string; close: string } | null;
  sun?: { open: string; close: string } | null;
}
export const WEEKDAY_LABELS: Record<WeekdayKey, string> = {
  mon: 'Montag',
  tue: 'Dienstag',
  wed: 'Mittwoch',
  thu: 'Donnerstag',
  fri: 'Freitag',
  sat: 'Samstag',
  sun: 'Sonntag',
};
export const WEEKDAY_ORDER: WeekdayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export interface CreateLocationInput {
  name: string;
  color?: string;
  isActive?: boolean;
  companyId?: string;
  address?: string | null;
  branchNumber?: string | null;
  openingHours?: OpeningHoursMap | null;
  managerCanSeeWages?: boolean;
  managerCanSeeBonusHours?: boolean;
  allowDoubleShift?: boolean;
  allowCrossLocationSwap?: boolean;
  regionId?: string | null;
  bundesland?: Bundesland | null;
}

export interface UpdateLocationInput extends Partial<CreateLocationInput> {}

export interface CreateTemplateInput {
  locationId: string;
  name: string;
  startTime: string;
  endTime: string;
  isOpenEnd?: boolean;
  isActive?: boolean;
}

export interface UpdateTemplateInput {
  name?: string;
  startTime?: string;
  endTime?: string;
  isOpenEnd?: boolean;
  isActive?: boolean;
}

export const locationsApi = {
  list: (token: string, withTemplates = false) =>
    apiFetch<{ locations: ApiLocation[] }>(
      `/api/locations${withTemplates ? '?withTemplates=1' : ''}`,
      { token },
    ).then((r) => r.locations),

  /** Einzelner Standort inkl. Vorlagen (für die Standort-Detail-Ansicht). */
  get: (token: string, id: string) =>
    apiFetch<{ location: ApiLocation }>(`/api/locations/${id}`, { token }).then((r) => r.location),

  create: (token: string, data: CreateLocationInput) =>
    apiFetch<{ location: ApiLocation }>('/api/locations', {
      method: 'POST',
      body: data,
      token,
    }).then((r) => r.location),

  update: (token: string, id: string, data: UpdateLocationInput) =>
    apiFetch<{ location: ApiLocation }>(`/api/locations/${id}`, {
      method: 'PUT',
      body: data,
      token,
    }).then((r) => r.location),

  /** Filialpasswort setzen/ändern (Kapitel 3.2, Screen 7) — nur Owner. */
  setPassword: (token: string, id: string, password: string) =>
    apiFetch<{ location: ApiLocation }>(`/api/locations/${id}/password`, {
      method: 'PUT',
      body: { password },
      token,
    }).then((r) => r.location),

  /** v1.3.0: Filialpasswort im Klartext abrufen (nur Chef) — `null`, wenn nicht gesetzt oder nur als Hash vorhanden. */
  getPassword: (token: string, id: string) =>
    apiFetch<{ password: string | null; reason?: 'not_set' | 'not_viewable' }>(`/api/locations/${id}/password`, {
      token,
    }),

  remove: (token: string, id: string) =>
    apiFetch<void>(`/api/locations/${id}`, { method: 'DELETE', token }),
};

export const templatesApi = {
  list: (token: string, locationId?: string) =>
    apiFetch<{ templates: ApiShiftTemplate[] }>(
      `/api/shift-templates${locationId ? `?locationId=${encodeURIComponent(locationId)}` : ''}`,
      { token },
    ).then((r) => r.templates),

  create: (token: string, data: CreateTemplateInput) =>
    apiFetch<{ template: ApiShiftTemplate }>('/api/shift-templates', {
      method: 'POST',
      body: data,
      token,
    }).then((r) => r.template),

  update: (token: string, id: string, data: UpdateTemplateInput) =>
    apiFetch<{ template: ApiShiftTemplate }>(`/api/shift-templates/${id}`, {
      method: 'PUT',
      body: data,
      token,
    }).then((r) => r.template),

  remove: (token: string, id: string) =>
    apiFetch<{ deleted: true; affectedShifts: number }>(`/api/shift-templates/${id}`, {
      method: 'DELETE',
      token,
    }),
};
